import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Calendar, TrashCan } from '@mint-lib/icons';
import { Illustration } from '@mint-lib/illustrations';
import { useQuery } from '@mint-lib/query-client';
import {
  Button,
  Card,
  Divider,
  typographyVariants,
} from '@mint-lib/tw-components';

import { useDeleteScheduledQueries } from '../../../hooks/useDeleteScheduledQueries.jsx';
import { useToggleScheduleDialog } from '../../../hooks/useToggleScheduleDialog.jsx';
import { getQueryScheduleOption } from '../../../queries/schedule.js';
import { ScheduleTableType } from '../../../types/queryScheduler.js';
import { ScheduleTable } from '../ScheduleTable/ScheduleTable.jsx';

const DrawerContent = () => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { data: queryScheduleData = [], isLoading } = useQuery(
    getQueryScheduleOption(),
  );
  const [selectedRows, setSelectedRows] = useState<ScheduleTableType[]>([]);

  const { setScheduledQueriesIds, toggleDeleteDialog } =
    useDeleteScheduledQueries();

  const { setIsOpenScheduleDialog } = useToggleScheduleDialog();
  const skeletonData: ScheduleTableType[] = useMemo(() => {
    return Array.from({ length: 10 }, (_, index) => ({
      id: `skeleton-${index}`,
      scheduledQueryId: '',
      chatSessionId: '',
      __isSkeleton: true,
      name: '',
      message: '',
      scheduling: 'monthly',
      status: 'active',
      startDate: '',
      endDate: '',
    }));
  }, []);

  const tableData = useMemo(() => {
    return queryScheduleData.map((s) => ({
      scheduledQueryId: s.scheduledQueryId,
      chatSessionId: s.chatSessionId,
      name: s.chatTitle,
      message: s.query,
      scheduling: s.scheduling,
      status: s.status,
      startDate: s.startDate,
      endDate: s.endDate,
    }));
  }, [queryScheduleData]);

  const handleDeleteSelectedQueries = () => {
    const queryIds = selectedRows.map((r) => r.scheduledQueryId);

    setScheduledQueriesIds(queryIds);
    toggleDeleteDialog();
  };

  const handleToggleScheduleFormDialog = () => {
    setIsOpenScheduleDialog((p) => !p);
  };

  const onSelectTableRows = useCallback((rows: ScheduleTableType[]) => {
    setSelectedRows(rows);
  }, []);

  if (!queryScheduleData.length) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex w-[200px] flex-col items-center gap-2">
          <Illustration className="size-40" component="IllustrationNoResults" />
          <h4 className={typographyVariants({ variant: 'h4' })}>
            {t('No scheduled query yet')}
          </h4>
          <Button onClick={handleToggleScheduleFormDialog}>
            <Calendar />
            {t('Schedule')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-2.5">
      <Card>
        <div className="flex flex-row gap-1">
          <Button onClick={handleToggleScheduleFormDialog}>
            <Calendar />
            {t('Schedule')}
          </Button>
          <Divider orientation="vertical" />
          <Button
            color="error"
            variant="outlined"
            onClick={handleDeleteSelectedQueries}
            disabled={!selectedRows.length}
          >
            <TrashCan />
            {t('Delete')}
          </Button>
        </div>
      </Card>
      <ScheduleTable
        tableData={isLoading ? skeletonData : tableData}
        isLoading={isLoading}
        onSelectTableRows={onSelectTableRows}
      />
    </div>
  );
};

export { DrawerContent };
